import { getReportsPathMap } from '@app/components/reports/utils';
import { pcfRoutes } from '@app/pcf/pcf-routes';
import { useGetAvailableIngredientsV2 } from '@shared/api';
import {
  AppBaseFoodsListIncludeItem,
  IngredientPickerItem,
} from '@shared/api/types';
import { Icons } from '@shared/components/content/icons';
import { useDebounce } from '@shared/components/data-table/hooks/use-debounce';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@shared/components/ui/form';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@shared/components/ui/popover';
import { Tabs, TabsList, TabsTrigger } from '@shared/components/ui/tabs';
import { cn, formatUnderscoreText } from '@shared/lib/utils';
import { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { generatePath, Link } from 'react-router-dom';
import Text from '../content/text';

const TYPE_ORDER = ['ingredient', 'purchased_good', 'product', 'base_food'];

const TYPE_CONFIG: Record<
  string,
  {
    icon: keyof typeof Icons;
    label: string;
    emptyLabel: string;
    description: string;
    color: string;
  }
> = {
  ingredient: {
    icon: 'carrot',
    label: 'Ingredient',
    emptyLabel: 'My ingredients',
    description:
      'Select from your existing ingredients over our basic foods to reuse common ingredients across recipes',
    color: 'text-teal-600',
  },
  purchased_good: {
    icon: 'shoppingCart',
    label: 'Purchased good',
    emptyLabel: 'My purchased goods',
    description:
      'Select an item from your purchase data to utilise the emissions modelling of your purchased item',
    color: 'text-teal-600',
  },
  product: {
    icon: 'salad',
    label: 'Product',
    emptyLabel: 'My products',
    description:
      'Select an existing product in your account to utilise the emissions modelling of your product',
    color: 'text-teal-600',
  },
  base_food: {
    icon: 'carrot',
    label: 'Basic food',
    emptyLabel: 'Basic foods',
    description: 'Select from our generic list of food and drink items',
    color: 'text-muted-foreground',
  },
};

const EMPTY_STATE_ORDER = [
  'purchased_good',
  'ingredient',
  'product',
  'base_food',
];

const PLURAL_TO_SINGULAR: Record<string, string> = {
  ingredients: 'ingredient',
  purchased_goods: 'purchased_good',
  products: 'product',
  base_foods: 'base_food',
};

const MODELLED_AS_LABELS: Record<string, string> = {
  modelled_food: 'Recipe',
};

function TypeIcon({ type }: { type: string }) {
  const config = TYPE_CONFIG[type];
  if (!config) return null;
  const Icon = Icons[config.icon];
  return <Icon className={cn('size-3 shrink-0', config.color)} />;
}

function buildHeaderExtra(item: IngredientPickerItem): string | null {
  if (!item.modelledAsType && !item.modelledAsName) return null;

  if (item.modelledAsType === 'purchased_good' && item.modelledAsName) {
    return `Matched to: ${item.modelledAsName}`;
  }

  if (item.modelledAsType === 'base_food' && item.modelledAsName) {
    return `Matched to: ${item.modelledAsName}`;
  }

  if (item.modelledAsType) {
    const typeLabel =
      MODELLED_AS_LABELS[item.modelledAsType] ??
      formatUnderscoreText(item.modelledAsType);
    return `Modelled by: ${typeLabel}`;
  }

  return null;
}

function getItemLink(item: IngredientPickerItem): string | null {
  if (item.type === 'ingredient') {
    return generatePath(pcfRoutes.PRODUCT_COMPONENTS_INGREDIENTS_DETAIL, {
      ingredient_uuid: item.uuid,
    });
  }
  if (item.type === 'product') {
    return generatePath(pcfRoutes.PRODUCT_DETAIL, { product_id: item.uuid });
  }
  if (item.type === 'purchased_good' && item.activityUuid) {
    return generatePath(getReportsPathMap()['purchase'].foodItem!, {
      item_uuid: item.activityUuid,
    });
  }
  return null;
}

interface IngredientPickerProps {
  name: string;
  label?: string;
  showLabel?: boolean;
  className?: string;
  includeErrorMessage?: boolean;
  testId?: string;
  include?: AppBaseFoodsListIncludeItem[];
  onSelect?: (item: IngredientPickerItem) => void;
  knownSelectedItem?: IngredientPickerItem;
  disabled?: boolean;
  /** Placeholder for the trigger; also shown while disabled. */
  placeholder?: string;
  /**
   * Render the popover as modal. Required when the picker sits inside a
   * dialog, so its dropdown scrolls and overlays correctly.
   */
  modal?: boolean;
}

export default function IngredientPicker({
  name,
  label = 'Ingredient',
  showLabel = false,
  className,
  includeErrorMessage,
  testId,
  include = ['ingredients', 'products', 'base_foods', 'purchased_goods'],
  onSelect,
  knownSelectedItem,
  disabled = false,
  placeholder = 'Select an item',
  modal = false,
}: IngredientPickerProps) {
  const { control, setValue } = useFormContext();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  const [selectedItem, setSelectedItem] = useState<IngredientPickerItem | null>(
    null
  );

  const debouncedSearch = useDebounce(search, 400);

  const includedTypes = TYPE_ORDER.filter((type) =>
    include.some((inc) => (PLURAL_TO_SINGULAR[inc] ?? inc) === type)
  );

  const showTabs = includedTypes.length > 1;

  const { data: ingredients, isLoading } = useGetAvailableIngredientsV2({
    search: debouncedSearch || undefined,
    include,
  });

  const visibleItems =
    activeTab === 'all'
      ? (ingredients ?? [])
      : (ingredients ?? []).filter((i) => i.type === activeTab);

  const emptyStateTypes = EMPTY_STATE_ORDER.filter((type) =>
    include.some((inc) => (PLURAL_TO_SINGULAR[inc] ?? inc) === type)
  );

  const handleSelect = (item: IngredientPickerItem) => {
    setSelectedItem(item);
    setValue(name, item.uuid);
    onSelect?.(item);
    setOpen(false);
    setSearch('');
  };

  return (
    <FormField
      control={control}
      name={name}
      render={({ field, fieldState }) => {
        const renderItem = (item: IngredientPickerItem, index: number) => {
          const headerExtra = buildHeaderExtra(item);
          const link = getItemLink(item);
          return (
            <li
              key={item.uuid}
              data-testid={testId ? `${testId}-option-${index}` : undefined}
              onClick={() => handleSelect(item)}
              className={cn(
                'flex min-h-[52px] cursor-pointer items-center gap-2 px-3 py-2 text-sm hover:bg-accent hover:text-accent-foreground',
                field.value === item.uuid && 'bg-accent'
              )}
            >
              <span className="min-w-0 flex-1 overflow-hidden">
                <span className="flex items-center gap-1">
                  <TypeIcon type={item.type} />
                  <Text variant="subtle" className="text-xs">
                    {TYPE_CONFIG[item.type]?.label ??
                      formatUnderscoreText(
                        PLURAL_TO_SINGULAR[item.type] ?? item.type
                      )}
                  </Text>
                  {item.externalId && (
                    <Text variant="subtle" className="text-xs">
                      | {item.externalId}
                    </Text>
                  )}
                  {headerExtra && (
                    <Text variant="subtle" className="text-xs">
                      | {headerExtra}
                    </Text>
                  )}
                </span>
                <Text
                  includeHover
                  className="line-clamp-1 max-w-xl text-ellipsis"
                >
                  {item.name}
                </Text>
              </span>
              {link && (
                <Link
                  to={link}
                  target="_blank"
                  rel="noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="shrink-0"
                >
                  <Icons.linkExternal className="size-4 text-teal hover:opacity-100" />
                </Link>
              )}
            </li>
          );
        };

        return (
          <FormItem className={cn('grid flex-1 items-center', className)}>
            {showLabel && <FormLabel>{label}</FormLabel>}
            <FormControl>
              <Popover open={open} onOpenChange={setOpen} modal={modal}>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    data-testid={testId}
                    disabled={disabled}
                    className={cn(
                      'flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
                      fieldState.error && 'border-destructive'
                    )}
                  >
                    {(selectedItem ?? knownSelectedItem)?.name && !disabled ? (
                      <span className="max-w-72 truncate">
                        {(selectedItem ?? knownSelectedItem)!.name}
                      </span>
                    ) : (
                      <span className="max-w-72 truncate text-muted-foreground">
                        {placeholder}
                      </span>
                    )}
                    <Icons.chevronDown className="ml-2 size-4 shrink-0 opacity-50" />
                  </button>
                </PopoverTrigger>
                <PopoverContent
                  className="w-[580px] p-0"
                  align="start"
                  onOpenAutoFocus={(e) => e.preventDefault()}
                >
                  <div className="flex items-center border-b px-3">
                    <Icons.search className="mr-2 size-4 shrink-0 opacity-50" />
                    <input
                      placeholder="Search items..."
                      data-testid={testId ? `${testId}-search` : undefined}
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      className="flex h-10 w-full bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground"
                      autoFocus
                    />
                  </div>

                  {!debouncedSearch ? (
                    <div className="px-4 py-5">
                      <p className="mb-4 text-center text-sm text-foreground">
                        Start typing to search from:
                      </p>
                      <div className="grid grid-cols-2 gap-2 [&>*:last-child:nth-child(odd)]:col-span-2">
                        {emptyStateTypes.map((type) => {
                          const config = TYPE_CONFIG[type];
                          if (!config) return null;
                          const Icon = Icons[config.icon];
                          return (
                            <div
                              key={type}
                              className="flex flex-col gap-2 rounded-lg border p-3 text-left"
                            >
                              <div className="flex items-center gap-2">
                                <Icon
                                  className={cn(
                                    ' size-4 shrink-0',
                                    config.color
                                  )}
                                />
                                <Text className="font-medium">
                                  {config.emptyLabel}
                                </Text>
                              </div>
                              <div className="flex flex-col gap-2">
                                <Text variant="subtle">
                                  {config.description}
                                </Text>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ) : isLoading ? (
                    <div className="py-6 text-center text-sm text-muted-foreground">
                      Searching...
                    </div>
                  ) : !ingredients?.length ? (
                    <div className="py-6 text-center text-sm text-muted-foreground">
                      {debouncedSearch.length <= 2
                        ? 'Keep typing to see results...'
                        : 'No items found.'}
                    </div>
                  ) : (
                    <>
                      {showTabs && (
                        <Tabs value={activeTab} onValueChange={setActiveTab}>
                          <TabsList className="h-auto w-full justify-start gap-0 overflow-x-auto rounded-none border-b bg-transparent p-0">
                            <TabsTrigger
                              value="all"
                              className="rounded-none border-b-2 border-transparent px-3.5 py-2 text-xs font-medium text-muted-foreground shadow-none data-[state=active]:border-teal-600 data-[state=active]:bg-transparent data-[state=active]:text-foreground data-[state=active]:shadow-none"
                            >
                              All
                            </TabsTrigger>
                            {includedTypes.map((type) => {
                              const config = TYPE_CONFIG[type];
                              const Icon = config ? Icons[config.icon] : null;
                              const isEmpty =
                                (ingredients ?? []).filter(
                                  (i) => i.type === type
                                ).length === 0;
                              return (
                                <TabsTrigger
                                  key={type}
                                  value={type}
                                  disabled={isEmpty}
                                  className="flex items-center gap-1.5 rounded-none border-b-2 border-transparent px-3.5 py-2 text-xs font-medium text-muted-foreground shadow-none disabled:opacity-40 data-[state=active]:border-teal-600 data-[state=active]:bg-transparent data-[state=active]:text-foreground data-[state=active]:shadow-none"
                                >
                                  {Icon && <Icon className="size-3 shrink-0" />}
                                  {config?.emptyLabel ?? type}
                                </TabsTrigger>
                              );
                            })}
                          </TabsList>
                        </Tabs>
                      )}
                      {visibleItems.length === 0 ? (
                        <div className="py-6 text-center text-sm text-muted-foreground">
                          No results in this category.
                        </div>
                      ) : (
                        <ul className="max-h-64 overflow-y-auto">
                          {visibleItems.map(renderItem)}
                        </ul>
                      )}
                    </>
                  )}
                </PopoverContent>
              </Popover>
            </FormControl>
            {includeErrorMessage && <FormMessage />}
          </FormItem>
        );
      }}
    />
  );
}
